import { makeAutoObservable } from "mobx";
import ApiManager from "../helpers/ApiManager";

/**
 * Authentication store for managing user authentication state and operations
 * Handles login, logout, token management, and user session persistence
 */
class authStore {

  /** @type {Object|null} Current authenticated user object */
  user = null;

  /** @type {string|null} JWT authentication token */
  token = null;

  /** @type {boolean} Whether user is currently authenticated */
  isAuthenticated = false;

  /**
   * Initialize the authentication store
   * Sets up MobX observables and attempts to restore session from storage
   */
  constructor() {
    makeAutoObservable(this);
    this.initializeAuth();
  }

  /**
   * Initialize authentication state from stored session data
   * Checks for existing user data in localStorage and token in localStorage or sessionStorage
   * @async
   * @returns {Promise<void>}
   */
  async initializeAuth() {
    try {
      const token = this.getToken();
      const user = localStorage.getItem('user');
      if (token && user) {
        this.isAuthenticated = true;
        this.user = JSON.parse(user);
        this.token = token;
      }
    } catch (error) {
      // console.log("🚀 ~ AuthStore.js:44 ~ authStore ~ initializeAuth ~ error:", error);
    }
  }

  /**
   * Authenticate user with email and password
   * If rememberMe is true, token is stored in localStorage
   * If rememberMe is false, token is stored in sessionStorage
   * @async
   * @param {string} email - User's email address
   * @param {string} password - User's password
   * @param {boolean} rememberMe - Whether to persist session in localStorage
   * @returns {Promise<Object>} API response with success status and user data
   */
  async login(email, password, rememberMe) {
    try {
      const response = await ApiManager.requestWithoutToken('auth/login', 'POST', { email, password });

      if (response.success) {
        this.user = response.data.user;
        this.token = response.data.token;
        localStorage.setItem('user', JSON.stringify(this.user));

        if (rememberMe) {
          localStorage.setItem('token', this.token);
        } else {
          sessionStorage.setItem('token', this.token);
        }
        return response;
      } else {
        this.isAuthenticated = false;
        localStorage.removeItem('user');
        this.removeToken();
        return response;
      }
    } catch (error) {
      // console.log("🚀 ~ AuthStore.js:79 ~ authStore ~ login ~ error:", error);
      this.isAuthenticated = false;
      localStorage.removeItem('user');
      this.removeToken();
      return {
        success: false,
        message: error.response.data.message,
      };
    }
  }

  /**
   * Retrieve authentication token from storage
   * Checks localStorage first, then sessionStorage
   * @returns {string|null} Authentication token or null if not found
   */
  getToken() {
    if (localStorage.getItem('token')) {
      return localStorage.getItem('token');
    } else {
      return sessionStorage.getItem('token');
    }
  }

  /**
   * Remove authentication token from all storage locations
   * Clears both localStorage and sessionStorage
   * @returns {void}
   */
  removeToken() {
    localStorage.removeItem('token');
    sessionStorage.removeItem('token');
  }

  /**
   * Logout current user and clear all authentication data
   * Resets user state and removes stored session data
   * @async
   * @returns {Promise<void>}
   */
  async logout() {
    try {
      this.user = null;
      this.token = null;
      this.isAuthenticated = false;
      localStorage.removeItem('user');
      this.removeToken();
    } catch (error) {
      // console.log("🚀 ~ AuthStore.js:~logout ~ error:", error);
      // Still clear the in-memory state even if localStorage fails
      this.user = null;
      this.token = null;
      this.isAuthenticated = false;
    }
  }

}

const AuthStore = new authStore();

export default AuthStore;